import { Clock, MapPin, Phone, Star } from "lucide-react";
import { Reveal, MaskLine, FadeIn } from "../components/Reveal";
import { CLINIC } from "../data/catalog";
import { WhatsAppGlyph } from "../components/WhatsAppFloat";

const ClinicPage = () => (
  <div className="pt-36 md:pt-44" data-testid="clinic-page">
    <header className="mx-auto max-w-7xl px-4 md:px-8">
      <FadeIn>
        <div className="flex items-center gap-4">
          <span className="h-px w-12 bg-ink/30" />
          <p className="eyebrow">The clinic</p>
        </div>
      </FadeIn>
      <h1 className="mt-8 font-serif font-light leading-[1.05] text-[11vw] sm:text-6xl lg:text-7xl">
        <MaskLine delay={0.15}>Ayushi Ayurveda</MaskLine>
        <MaskLine delay={0.28}>
          <span className="italic text-sage-deep">Wellness.</span>
        </MaskLine>
      </h1>
      <FadeIn delay={0.5} className="mt-8 max-w-xl">
        <p className="text-base leading-relaxed text-ink/70 md:text-lg">
          An Ayurvedic clinic at Chavadimukku, Sreekaryam, run by Dr. Shibina Shemim. Consultations
          run Monday to Saturday. If you are in Trivandrum, come in — you can buy the products here
          and talk to the doctor who makes them.
        </p>
      </FadeIn>
    </header>

    <section className="mx-auto max-w-7xl px-4 py-16 md:px-8 md:py-20">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <div className="border-t hairline">
            {[
              { icon: MapPin, label: "Address", value: CLINIC.address },
              { icon: Phone, label: "Phone & WhatsApp", value: CLINIC.phone, href: `tel:${CLINIC.phoneRaw}` },
              { icon: Clock, label: "Open", value: CLINIC.hours },
              { icon: Clock, label: "Consultation hours", value: CLINIC.consult },
            ].map((r, i) => (
              <Reveal key={r.label} delay={i * 0.06}>
                <div className="flex gap-5 border-b hairline py-6" data-testid={`clinic-row-${r.label.toLowerCase().replace(/\s|&/g, "-")}`}>
                  <r.icon className="mt-1 h-5 w-5 shrink-0 text-sage-deep" strokeWidth={1.5} />
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.24em] text-ink/45">{r.label}</p>
                    {r.href ? (
                      <a href={r.href} className="mt-2 block text-[15px] text-ink/80 transition-colors hover:text-ink">{r.value}</a>
                    ) : (
                      <p className="mt-2 max-w-md text-[15px] leading-relaxed text-ink/80">{r.value}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="flex gap-5 border-b hairline py-6">
                <Star className="mt-1 h-5 w-5 shrink-0 fill-gold text-gold" />
                <div>
                  <p className="text-[11px] uppercase tracking-[0.24em] text-ink/45">On Google</p>
                  <a href={CLINIC.googleUrl} target="_blank" rel="noreferrer" className="mt-2 block text-[15px] text-ink/80 underline-offset-4 transition-colors hover:text-ink hover:underline" data-testid="clinic-google-link">
                    {CLINIC.rating} across {CLINIC.reviewCount} reviews
                  </a>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.35}>
            <p className="mt-8 text-[13px] leading-relaxed text-ink/55" data-testid="clinic-note">
              The clinic is consultation-only while the treatment centre is renovated. Therapies
              are expected to resume in January.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={CLINIC.googleUrl} target="_blank" rel="noreferrer" data-testid="clinic-directions-cta" className="rounded-full bg-ink px-7 py-3.5 text-[12px] uppercase tracking-[0.18em] text-cream transition-colors hover:bg-ink-deep">
                Get Directions
              </a>
              <a href={`tel:${CLINIC.phoneRaw}`} data-testid="clinic-call-cta" className="rounded-full border border-ink/30 px-7 py-3.5 text-[12px] uppercase tracking-[0.18em] transition-colors hover:border-ink">
                Call the Clinic
              </a>
              <a href={CLINIC.whatsapp} target="_blank" rel="noreferrer" data-testid="clinic-whatsapp-cta" className="inline-flex items-center gap-2 rounded-full border border-ink/30 px-7 py-3.5 text-[12px] uppercase tracking-[0.18em] transition-colors hover:border-ink">
                <WhatsAppGlyph className="h-4 w-4" /> WhatsApp
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-5 lg:col-start-8">
          <div className="relative overflow-hidden border hairline bg-ink p-8 text-cream" data-testid="clinic-map">
            <svg viewBox="0 0 400 300" className="absolute inset-0 h-full w-full opacity-20" preserveAspectRatio="none">
              {[...Array(8)].map((_, i) => (
                <path key={i} d={`M-20 ${30 + i * 38} C 100 ${10 + i * 42}, 300 ${50 + i * 34}, 420 ${26 + i * 40}`} fill="none" stroke="#9DB4A0" strokeWidth="0.8" />
              ))}
            </svg>
            <div className="relative flex min-h-[320px] flex-col justify-between">
              <MapPin className="h-6 w-6 text-gold" />
              <div>
                <p className="font-serif text-xl italic">Chavadimukku, Sreekaryam</p>
                <p className="mt-2 max-w-xs text-sm leading-relaxed text-cream/60">
                  Ground floor of Jas Buildings, a short walk from the Sreekaryam junction. The live
                  Google map embeds here at launch.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  </div>
);

export default ClinicPage;
